import { forwardRef, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { Grid, type GridRef } from '@/components/core/Grid/Grid';
import { fieldPathToGridStateKey } from '@/components/core/Grid/useGridPreferences';
import { DataModelAttribute } from '@/types/DataModel';

import { generateInitialSortingState } from '../../Collection/sorting';

interface MultiReferenceGridProps {
    columns: any[];
    data: any[];
    fieldPath: string;
    memberAttributes: DataModelAttribute[];
    attribute: DataModelAttribute;
    enableSorting: boolean;
}

export const MultiReferenceGrid = forwardRef<GridRef, MultiReferenceGridProps>(({
    columns,
    data,
    fieldPath,
    memberAttributes,
    attribute,
    enableSorting
}, ref) => {
    const paginationThreshold = 10;
    const gridBlockRef = useRef<HTMLDivElement>(null);
    // Natural height of the Grid block with a full first page (10 rows + pager).
    // Locked after first measure so shorter last pages do not shrink the block.
    const [lockedGridHeight, setLockedGridHeight] = useState<number>();

    const shouldShowPagination = (dataLength: number): boolean => {
        return dataLength > paginationThreshold;
    };

    const initialSortingState = useMemo(() => generateInitialSortingState(attribute, memberAttributes), [attribute, memberAttributes]);

    const dataLength = data?.length ?? 0;
    const shouldShowPaginationFlag = shouldShowPagination(dataLength);

    // Remeasure when the attribute/field changes.
    useLayoutEffect(() => {
        setLockedGridHeight(undefined);
    }, [fieldPath]);

    // Measure the Grid block once it has rendered a full first page, then lock
    // that height. Cleared when pagination disappears (≤ 10 entries).
    useLayoutEffect(() => {
        if (!shouldShowPaginationFlag) {
            setLockedGridHeight(undefined);
            return;
        }
        if (lockedGridHeight != null) {
            return;
        }

        const height = gridBlockRef.current?.getBoundingClientRect().height ?? 0;
        if (height > 0) {
            setLockedGridHeight(height);
        }
    }, [shouldShowPaginationFlag, lockedGridHeight, dataLength]);

    return (
        <div className="tw-flex tw-flex-col">
            <div
                ref={gridBlockRef}
                className="tw-flex tw-flex-col"
                style={lockedGridHeight != null ? { height: lockedGridHeight } : undefined}
            >
                <Grid
                    ref={ref}
                    columns={columns}
                    data={data || []}
                    insideItemEditor={true}
                    enableSorting={enableSorting}
                    initialSortingState={initialSortingState}
                    storedStateKey={fieldPathToGridStateKey(fieldPath)}
                    enableRowSelection={false}
                    className="tw-bg-white tw-flex-1"
                    shouldShowPaginationFlag={shouldShowPaginationFlag}
                    paginationNavigation="full"
                />
            </div>
        </div>
    );
});

MultiReferenceGrid.displayName = 'MultiReferenceGrid';
